/**
 * 64.11: shells on the action clock. A seated gunner fires the seat's weapon; the shell flies one tick at a time by
 * the turn game's own gravity step and terrain test (core/flight.ts simulateFlightSegment: rk4Step at PHYS_DT, then
 * checkCollision), so a shot flies the same way on either clock. It is the planet family (SCALE.md §6): full gravity,
 * the coarse mesh, no fine ground. Geysers, wormholes and direct hits on people are left out: a shell lands, and its
 * blast does the work (resolved by the Match through core/resolveImpact.ts). Pure.
 */

import type { ActionShell, FireParams, GameState, PlayerState, ProjectileState, Vec3, WeaponKey } from '../types';
import { MOUNT_INTEL } from '../constants/action';
import { OUT_OF_BOUNDS, PHYS_DT, SHELL_MAX_SECONDS, WEAPONS } from '../constants';
import { createProjectile, computeLaunchDirection } from './gameState';
import { getSurfaceRadius } from './surface';
import { rk4Step, checkCollision } from './physics';
import { vecAdd, vecLength, vecNormalize, vecScale, vecSub, vecDot } from '../math';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { hasLineOfSight, losEyePoint } from './los';
import { l0SightRadius, l0Horizon } from './teamFog';
import { weatherSees } from './perception';

/** Fire a shell from `gunner`'s seat on `structureId`, laid by `params`. */
export function fireShell(state: GameState, gunner: PlayerState, structureId: number, params: FireParams): { state: GameState; shell: ActionShell } {
  const p = createProjectile(gunner, state.planets, params, state.config);
  const id = state.shellsFired ?? 0;
  const shell: ActionShell = { id, ownerId: gunner.id, structureId, weaponKey: params.weaponKey, pos: p.pos, vel: p.vel, ticks: 0 };
  return { state: { ...state, shells: [...(state.shells ?? []), shell], shellsFired: id + 1 }, shell };
}

export interface ShellImpact { shell: ActionShell; planetIndex: number; point: Vec3 }

/** One tick for every shell in flight: the ones still flying, the ones that hit a planet, the ones lost (a star, a wormhole, out of bounds, too long). */
export function stepShells(state: Pick<GameState, 'shells' | 'planets'>): { shells: ActionShell[]; impacts: ShellImpact[]; lost: ActionShell[] } {
  const flying: ActionShell[] = [], impacts: ShellImpact[] = [], lost: ActionShell[] = [];
  const maxTicks = Math.round(SHELL_MAX_SECONDS / PHYS_DT);
  for (const s of state.shells ?? []) {
    const r = rk4Step(s.pos, s.vel, PHYS_DT, state.planets);
    const shell: ActionShell = { ...s, pos: r.pos, vel: r.vel, ticks: s.ticks + 1 };
    const c = checkCollision(shell.pos, state.planets);
    if (c) {
      const t = state.planets[c.planetIndex]?.type;
      if (t === 'star' || t === 'wormhole') lost.push(shell);
      else impacts.push({ shell, planetIndex: c.planetIndex, point: c.point });
      continue;
    }
    const d = Math.sqrt(shell.pos.x * shell.pos.x + shell.pos.y * shell.pos.y + shell.pos.z * shell.pos.z);
    if (d > OUT_OF_BOUNDS || shell.ticks >= maxTicks) { lost.push(shell); continue; }
    flying.push(shell);
  }
  return { shells: flying, impacts, lost };
}

/** A shell as the impact resolver reads a projectile (core/resolveImpact.ts). */
export function shellProjectile(s: ActionShell): ProjectileState {
  return { pos: s.pos, vel: s.vel, thrustDir: s.vel, thrustMag: 0, fuelRemaining: 0, weaponKey: s.weaponKey, phase: 'ballistic', time: s.ticks * PHYS_DT, ownerId: s.ownerId, lastWormholeId: null, wormholeTransits: 0 };
}

/**
 * 69.9 step 5: the shot from `gunner`'s seat that lands nearest `point` on its planet with `weaponKey` — the azimuth that bears
 * on the point (the launch frame scanned, then refined), the elevation MOUNT_INTEL.gunElevation, the power by bisection on
 * the landing's distance along the bearing (MOUNT_INTEL.gunSolveSteps flights). Null when no power reaches it within
 * `withinPeople`. Deterministic; a gun crew's shot at a known mount's radar head.
 */
export function solveShellAt(state: Pick<GameState, 'planets' | 'config'>, gunner: PlayerState, point: Vec3, weaponKey: WeaponKey, withinPeople = 3): FireParams | null {
  const planet = state.planets[gunner.planetIndex], n = vecNormalize(gunner.surfaceDir);
  const here = vecAdd(planet.position, vecScale(n, getSurfaceRadius(planet, n)));
  const toPt = vecSub(point, here), flat = vecNormalize(vecSub(toPt, vecScale(n, vecDot(toPt, n))));
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  // the azimuth: the launch direction's flat part scanned for the best bearing, then refined about it
  const bearing = (az: number) => { const d = computeLaunchDirection(n, az, 0); return vecDot(d, flat); };
  let az = 0, bestB = -Infinity;
  for (let k = 0; k < 72; k++) { const a = (k / 72) * 2 * Math.PI, b = bearing(a); if (b > bestB) { bestB = b; az = a; } }
  for (let step = Math.PI / 72; step > 1e-4; step /= 2) { const l = bearing(az - step), r = bearing(az + step); if (l > bestB) { bestB = l; az -= step; } else if (r > bestB) { bestB = r; az += step; } }
  // the power: too much lands past the point along the bearing, too little short — bisected
  const along = (params: FireParams): number | null => {
    const land = predictShellLanding(state, gunner, params);
    if (!land || land.planetIndex !== gunner.planetIndex) return null;
    const lp = vecSub(land.point, here);
    return vecDot(lp, flat) - vecDot(toPt, flat);   // (world units past the point along the bearing; negative: short)
  };
  const elevation = MOUNT_INTEL.gunElevation, cap = Math.min(WEAPONS[weaponKey].maxPower ?? 100, 100);
  let lo = 5, hi = cap, best: FireParams | null = null, bestErr = Infinity;
  for (let i = 0; i < MOUNT_INTEL.gunSolveSteps; i++) {
    const power = (lo + hi) / 2, params: FireParams = { azimuth: az, elevation, power, weaponKey };
    const err = along(params);
    if (err === null) { hi = power; continue; }   // (lost: too much)
    if (Math.abs(err) < bestErr) { bestErr = Math.abs(err); best = params; }
    if (err > 0) hi = power; else lo = power;
  }
  return best && bestErr <= withinPeople * person ? best : null;
}

/** Where a shell laid by `params` from `gunner`'s seat would land (the planet and the point), or null if it would be lost. */
export function predictShellLanding(state: Pick<GameState, 'planets' | 'config'>, gunner: PlayerState, params: FireParams): { planetIndex: number; point: Vec3 } | null {
  const p = createProjectile(gunner, state.planets, params, state.config);
  let probe: Pick<GameState, 'shells' | 'planets'> = { planets: state.planets, shells: [{ id: -1, ownerId: gunner.id, structureId: -1, weaponKey: params.weaponKey, pos: p.pos, vel: p.vel, ticks: 0 }] };
  for (;;) {
    const r = stepShells(probe);
    if (r.impacts.length > 0) return { planetIndex: r.impacts[0].planetIndex, point: r.impacts[0].point };
    if (r.shells.length === 0) return null;
    probe = { planets: state.planets, shells: r.shells };
  }
}

/**
 * Decision 128 (the owner: "Accuracy penalty without spotter, and slower reload without spotter"): does anyone of the
 * gunner's team see `point` now — someone standing there, to the eye of a teammate (the gunner too, from the gun),
 * within its sight (core/teamFog.ts l0SightRadius: the weather's limit, near its horizon), terrain in the way.
 */
export function aimSpotted(state: GameState, gunner: PlayerState, planetIndex: number, point: Vec3, tick: number): boolean {
  const planet = state.planets[planetIndex];
  if (!planet) return false;
  const ps = personSetOf(state);
  const up = vecNormalize(vecSub(point, planet.position));
  const target = vecAdd(point, vecScale(up, ps.losEyeHeight));
  for (const q of state.players) {
    if (!q.alive || q.teamIndex !== gunner.teamIndex || q.planetIndex !== planetIndex) continue;
    const eye = losEyePoint(q, state.planets, ps.losEyeHeight);
    // within the horizon cap, and the weather lets it see there (62.54 step 6: the fog along the line; today's range with weatherCells off)
    if (vecLength(vecSub(target, eye)) > l0Horizon(state, q)) continue;
    if (!weatherSees(state, planetIndex, eye, target, tick * PHYS_DT, l0SightRadius(state, q, tick))) continue;
    if (hasLineOfSight(eye, target, state.planets)) return true;
  }
  return false;
}
